import React from 'react';
import { ExternalLink } from 'lucide-react';
import { ActivityItem } from '../../types';
import { linkOrigin, sourceIconOf } from '../../utils/systemSource';
import { formatDateLabel } from '../../utils/date';

/**
 * 봇 대화(SystemFeedView)의 카드 말풍선 1개 — 외부 시스템 알림(전자결재 등) 1건.
 * 형태는 채팅 말풍선(MessageItem의 상대 메시지)과 같은 좌측 정렬 + 아바타 + 이름·시각 행이고,
 * 본문은 Teams 결재 카드처럼 '알림 제목 → 문서 제목 → 원점 → 문서 열기' 순서다.
 *
 * 색: 첨부 파일 카드와 같은 표면(흰 카드 + slate 테두리), 버튼은 중립 아웃라인.
 * 카드마다 브랜드 레드 채움이 쌓이면 경고로 읽힌다(레드는 액션·경고·안읽음 점 전용).
 * 링크는 원점까지만 보인다(문서 경로·id 비노출 — 알림함 목록과 같은 규칙).
 */

const TIME_FORMATTER = new Intl.DateTimeFormat('ko-KR', { hour: 'numeric', minute: '2-digit' });

interface SystemNoticeCardProps {
  item: ActivityItem;
  /** 같은 출처가 5분 안에 이어 보낸 카드 — 아바타·이름 행 생략(채팅 그룹핑과 같은 규칙) */
  isContinuation: boolean;
  showDateSeparator: boolean;
  isHighlighted: boolean;
  onOpenDocument: (item: ActivityItem) => void;
  registerRef: (id: number, el: HTMLDivElement | null) => void;
}

export const SystemNoticeCard: React.FC<SystemNoticeCardProps> = React.memo(({
  item,
  isContinuation,
  showDateSeparator,
  isHighlighted,
  onOpenDocument,
  registerRef,
}) => {
  const Icon = sourceIconOf(item.source);
  const origin = linkOrigin(item.link);
  const time = TIME_FORMATTER.format(new Date(item.createdAt));

  return (
    <>
      {showDateSeparator && (
        <div className="flex items-center gap-3 py-2">
          <div className="flex-1 border-t border-slate-300" />
          <span className="text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap text-slate-500 bg-slate-50">
            {formatDateLabel(item.createdAt)}
          </span>
          <div className="flex-1 border-t border-slate-300" />
        </div>
      )}
      <div
        ref={(el) => registerRef(item.id, el)}
        className={`flex gap-2 md:gap-4 -mx-4 px-4 rounded-lg transition-colors duration-700 ${
          isContinuation ? 'pt-1.5' : 'mt-3 md:mt-5 first:mt-0 pt-1'
        } pb-1 ${isHighlighted ? 'bg-brand-100' : ''}`}
      >
        {isContinuation ? (
          <div className="w-10 shrink-0" aria-hidden="true" />
        ) : (
          <span className="h-10 w-10 rounded-full flex items-center justify-center shrink-0 self-start bg-blue-100 text-blue-600">
            <Icon size={18} />
          </span>
        )}
        <div className="flex flex-col max-w-[calc(100%-3.5rem)] md:max-w-[70%] min-w-0 items-start">
          {!isContinuation && (
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-sm font-bold text-slate-700">{item.actorName}</span>
              <span className="text-xs text-slate-400">{time}</span>
            </div>
          )}
          <div className="flex items-end gap-1.5 min-w-0 max-w-full">
            <div
              className={`w-72 md:w-80 max-w-full min-w-0 bg-white border border-slate-200 shadow-sm overflow-hidden ${
                isContinuation ? 'rounded-2xl' : 'rounded-2xl rounded-tl-none'
              }`}
            >
              <div className="px-4 pt-3 pb-2.5">
                <p className="text-sm font-semibold text-slate-800 leading-snug break-anywhere">
                  {item.title || `${item.actorName} 알림`}
                </p>
                {item.preview && (
                  <p className="text-sm text-slate-600 leading-snug mt-1 break-anywhere">{item.preview}</p>
                )}
                {origin && <p className="text-xs text-slate-400 mt-1.5 truncate">{origin}</p>}
              </div>
              {item.link && (
                <button
                  type="button"
                  onClick={() => onOpenDocument(item)}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 border-t border-slate-100 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <ExternalLink size={14} />
                  문서 열기
                </button>
              )}
            </div>
            <span className="flex flex-col items-start gap-1 mb-0.5 shrink-0">
              {!item.readAt && (
                <span className="w-2 h-2 rounded-full bg-brand-600" role="img" aria-label="안 읽음" />
              )}
              {isContinuation && <span className="text-[11px] text-slate-400">{time}</span>}
            </span>
          </div>
        </div>
      </div>
    </>
  );
});

SystemNoticeCard.displayName = 'SystemNoticeCard';
